import { useEffect, useState } from "react";
import { api, parseApiError, fmtCurrency } from "../lib/api";

const STATUS_COLOR = {
  0: { label: "New", className: "pill pill-gold" },
  1: { label: "Rented", className: "pill pill-green" },
};
function statusPill(code, name) {
  const meta = STATUS_COLOR[code] || { label: name || `Status ${code}`, className: "pill pill-mute" };
  return <span className={meta.className} data-testid={`status-pill-${code ?? "x"}`}>{meta.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  } catch { return ""; }
}

export default function AdminReservations() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [status, setStatus] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [confirmResend, setConfirmResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendResult, setResendResult] = useState(null); // {kind:'ok'|'err', msg}

  const load = async () => {
    setLoading(true); setErr(null);
    try {
      const params = { page, pageSize };
      if (status !== "") params.status = Number(status);
      const r = await api.get("/admin/reservations", { params });
      setData(r.data);
    } catch (e) {
      setErr(parseApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  const items = data?.items || [];
  const total = data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <section data-testid="admin-reservations">
      <div className="admin-toolbar">
        <div className="admin-toolbar__title">Reservations</div>
        <div className="admin-toolbar__filters">
          <label className="admin-field">
            <span>Status</span>
            <select
              data-testid="admin-status-filter"
              value={status}
              onChange={(e) => { setPage(1); setStatus(e.target.value); }}
              className="admin-input"
            >
              <option value="">All</option>
              <option value="0">New (held)</option>
              <option value="1">Rented (paid)</option>
            </select>
          </label>
          <button data-testid="admin-refresh" onClick={load} className="admin-btn admin-btn-ghost">Refresh</button>
        </div>
      </div>

      {err && <div className="admin-err" data-testid="admin-reservations-err">{err}</div>}
      {loading && <div className="admin-loading">Loading…</div>}

      {!loading && !err && (
        <>
          <div className="admin-table-wrap" data-testid="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Guest</th>
                  <th>Email</th>
                  <th>Trip</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th className="ta-right">Total</th>
                  <th>Deposit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={10} className="admin-empty" data-testid="admin-empty">No reservations.</td></tr>
                )}
                {items.map((r) => {
                  const dep = r?.paymentSummary?.deposit || {};
                  const depPaid = Number(dep.paid || 0) > 0;
                  return (
                    <tr key={r.reservationID} data-testid={`row-${r.reservationID}`}>
                      <td className="mono">{r.confirmationCode || "—"}</td>
                      <td>{[r.renterFirstName, r.renterLastName].filter(Boolean).join(" ") || "—"}</td>
                      <td className="mute">{r.renterEmail || "—"}</td>
                      <td>{r.serviceDisplayName || r.assetName || "—"}</td>
                      <td>{fmtDate(r.startDateTime)}</td>
                      <td className="mute">{fmtTime(r.startDateTime)}{r.endDateTime ? `–${fmtTime(r.endDateTime)}` : ""}</td>
                      <td>{statusPill(r.reservationStatus, r.reservationStatusName)}</td>
                      <td className="ta-right">{fmtCurrency(r.totalAmount, r.currencyCode || "USD")}</td>
                      <td>
                        {depPaid
                          ? <span className="pill pill-green" data-testid={`dep-${r.reservationID}`}>Paid</span>
                          : <span className="pill pill-mute" data-testid={`dep-${r.reservationID}`}>—</span>}
                      </td>
                      <td><button data-testid={`view-${r.reservationID}`} className="admin-btn admin-btn-ghost" onClick={() => setDrawer(r)}>View</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="admin-pager" data-testid="admin-pager">
            <span className="mute">Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
            <div className="admin-pager__buttons">
              <button data-testid="page-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="admin-btn admin-btn-ghost">Prev</button>
              <span className="mono">{page} / {pages}</span>
              <button data-testid="page-next" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="admin-btn admin-btn-ghost">Next</button>
            </div>
          </div>
        </>
      )}

      {drawer && (
        <div className="admin-drawer-overlay" onClick={() => { setDrawer(null); setResendResult(null); setConfirmResend(false); }} data-testid="admin-drawer">
          <aside className="admin-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer__head">
              <div>
                <div className="admin-overline">Reservation</div>
                <div className="admin-drawer__code mono">{drawer.confirmationCode}</div>
              </div>
              <button data-testid="admin-drawer-close" onClick={() => { setDrawer(null); setResendResult(null); setConfirmResend(false); }} className="admin-btn admin-btn-ghost">Close</button>
            </div>

            <div className="admin-drawer__actions">
              <button
                data-testid="admin-action-resend"
                className="admin-btn admin-btn-primary"
                onClick={() => { setConfirmResend(true); setResendResult(null); }}
                disabled={resending}
              >
                {resending ? "Sending…" : "Resend confirmation email"}
              </button>
              <button
                data-testid="admin-action-refund"
                className="admin-btn"
                disabled
                title="Refund endpoint not yet available from RentAnything — operator action required."
              >
                Refund deposit (unavailable)
              </button>
            </div>

            {resendResult && (
              <div
                className={`admin-banner admin-banner--${resendResult.kind}`}
                data-testid="admin-resend-banner"
              >
                {resendResult.msg}
              </div>
            )}

            {confirmResend && !resendResult && (
              <div className="admin-banner admin-banner--prompt" data-testid="admin-resend-confirm">
                <div className="admin-banner__text">
                  Resend confirmation email to <b>{drawer.renterEmail}</b>?
                </div>
                <div className="admin-banner__buttons">
                  <button
                    data-testid="admin-resend-cancel"
                    className="admin-btn admin-btn-ghost"
                    onClick={() => setConfirmResend(false)}
                    disabled={resending}
                  >Cancel</button>
                  <button
                    data-testid="admin-resend-confirm-btn"
                    className="admin-btn admin-btn-primary"
                    disabled={resending}
                    onClick={async () => {
                      setResending(true);
                      try {
                        const r = await api.post(
                          `/admin/reservations/${drawer.reservationID}/resend-confirmation`,
                          { confirmationCode: drawer.confirmationCode, renterEmail: drawer.renterEmail },
                        );
                        const template = r.data?.template === "deposit_paid_to_guest" ? "confirmed" : "held";
                        setResendResult({ kind: "ok", msg: `Email sent to ${r.data?.sent_to || drawer.renterEmail} (${template} template).` });
                      } catch (e) {
                        setResendResult({ kind: "err", msg: parseApiError(e) || "Resend failed." });
                      } finally {
                        setResending(false);
                        setConfirmResend(false);
                      }
                    }}
                  >{resending ? "Sending…" : "Send"}</button>
                </div>
              </div>
            )}

            <pre className="admin-json" data-testid="admin-drawer-json">{JSON.stringify(drawer, null, 2)}</pre>
          </aside>
        </div>
      )}
    </section>
  );
}
